import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';
import PublicHeader from '../components/layout/PublicHeader';
import PublicFooter from '../components/layout/PublicFooter';
import config from '../config';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <Link
              to="/home"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-3 sm:mb-4 text-sm sm:text-base"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <div className="flex items-center mb-3 sm:mb-4">
              <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-primary-600 mr-2 sm:mr-3 flex-shrink-0" />
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">Terms and Conditions</h1>
            </div>
            <p className="text-sm sm:text-base text-gray-600">
              Last updated: March 3, 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-sm sm:prose-lg max-w-none">
            <section className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-3 sm:mb-4">1. Acceptance of Terms</h2>
              <p className="text-sm sm:text-base text-gray-700 mb-3 sm:mb-4">
                By accessing and using {config.appName} ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 mb-4">
                {config.appName} is a document intelligence platform designed for research-intensive industries. The Service allows users to upload, process, and query documents using artificial intelligence technology. The Service is intended for organizations that require extensive document research and analysis, including but not limited to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Legal professionals and law firms</li>
                <li>Government agencies</li>
                <li>Scientific research institutions</li>
                <li>Business organizations</li>
                <li>Other research-intensive entities</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>
              <p className="text-gray-700 mb-4">
                To access certain features of the Service, you must register for an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Maintain and update your information to keep it accurate, current, and complete</li>
                <li>Maintain the security of your password and identification</li>
                <li>Accept all responsibility for activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. AI-Generated Content and Reliance</h2>
              <p className="text-gray-700 mb-4">
                The Service uses artificial intelligence to analyze documents and generate responses. You acknowledge that:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>AI-generated answers are for assistance only and may contain errors or omissions</li>
                <li>You are responsible for verifying important results and not relying solely on AI output for legal, regulatory, or high-stakes decisions</li>
                <li>We do not guarantee the accuracy, completeness, or fitness for a particular purpose of AI-generated content</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use</h2>
              <p className="text-gray-700 mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Upload, transmit, or distribute any content that is illegal, harmful, threatening, abusive, or violates any applicable laws</li>
                <li>Infringe upon the intellectual property rights of others</li>
                <li>Upload malicious code, viruses, or any other harmful software</li>
                <li>Attempt to gain unauthorized access to the Service or its related systems</li>
                <li>Interfere with or disrupt the Service or servers connected to the Service</li>
                <li>Use the Service for any purpose that violates local, state, national, or international law</li>
                <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property Rights</h2>
              <p className="text-gray-700 mb-4">
                The Service and its original content, features, and functionality are owned by {config.appName} and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-gray-700 mb-4">
                You retain ownership of any content you upload to the Service. However, by uploading content, you grant {config.appName} a license to use, store, process, and analyze your content solely for the purpose of providing the Service to you.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Privacy and Security</h2>
              <p className="text-gray-700 mb-4">
                We are committed to protecting your privacy and the security of your data. Please review our Privacy Policy, which also governs your use of the Service, to understand our practices.
              </p>
              <p className="text-gray-700 mb-4">
                While we implement security measures to protect your data, you acknowledge that no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Subscription and Payment Terms</h2>
              <p className="text-gray-700 mb-4">
                If you subscribe to a paid plan:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Subscription fees are billed in advance on a recurring basis</li>
                <li>All fees are non-refundable except as required by law</li>
                <li>We reserve the right to change our pricing with 30 days' notice</li>
                <li>Failure to pay may result in suspension or termination of your account</li>
                <li>You are responsible for any taxes applicable to your subscription</li>
              </ul>
              <p className="text-gray-700 mb-4">
                Free trials or evaluation access (if offered) are subject to the same Terms. We may limit or discontinue trial offers at any time. Conversion to a paid plan may be required to retain access after the trial period.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Service Availability</h2>
              <p className="text-gray-700 mb-4">
                We strive to provide continuous availability of the Service but do not guarantee uninterrupted access. The Service may be unavailable due to:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2">
                <li>Scheduled maintenance</li>
                <li>Unforeseen technical issues</li>
                <li>Force majeure events</li>
                <li>Actions by third parties</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, {config.appName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
              </p>
              <p className="text-gray-700 mb-4">
                The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination</h2>
              <p className="text-gray-700 mb-4">
                We may terminate or suspend your account and access to the Service immediately, without prior notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.
              </p>
              <p className="text-gray-700 mb-4">
                Upon termination, your right to use the Service will immediately cease. We may delete your account and data, though we will provide reasonable notice and opportunity to export your data when possible.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 mb-4">
                We reserve the right to modify these Terms at any time. We will notify users of any material changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Governing Law</h2>
              <p className="text-gray-700 mb-4">
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved through appropriate legal channels.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Third-Party Services and Integrations</h2>
              <p className="text-gray-700 mb-4">
                The Service may integrate with third-party platforms (e.g., cloud storage, search consoles). Your use of those integrations is subject to the third party's terms and policies. We are not responsible for the availability, content, or practices of third-party services. You connect them at your own risk.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Contact Information</h2>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please contact us through our <Link to="/contact" className="text-primary-600 hover:text-primary-700 underline">Contact Us</Link> page.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Entire Agreement</h2>
              <p className="text-gray-700 mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and {config.appName} regarding the use of the Service and supersede all prior agreements and understandings.
              </p>
            </section>
          </div>

          {/* Footer Actions */}
          <div className="mt-8 pt-8 border-t border-gray-200">
            <Link
              to="/home"
              className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
};

export default TermsAndConditions;

